/*
 * Copyright (c) 2025 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {Link, showDialog} from '@dvrd/dvr-controls';
import SplitVAT from "./splitVAT";
import {ActivateHandle} from "../../../util/interfaces";

interface Props {
    onSubmit: (vats: Record<string, number>, countryCode: string, productId: string | Array<string>) => void;
}

export type SplitVATData = {percentage: string, value: string};

function SplitVATController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {onSubmit} = props;
    const countryCode = useRef<string>('NL');
    const productID = useRef<string | Array<string>>('');
    const [vats, setVats] = useState<Array<SplitVATData>>([]);
    const [active, setActive] = useState(false);

    function onActivate(_countryCode: string, _productID: string | Array<string>, splitVATS?: Record<string, number>) {
        countryCode.current = _countryCode;
        productID.current = _productID;
        let vats: Array<SplitVATData> = [];
        if (splitVATS && Object.keys(splitVATS).length)
            for (const [percentage, value] of Object.entries(splitVATS))
                vats.push({percentage, value: value.toString()});
        else vats = [{percentage: '-1', value: '0'}, {percentage: '-1', value: '0'}];
        setVats(vats);
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setVats([]);
        countryCode.current = '';
        productID.current = '';
    }

    function onChange(index: number) {
        return function(value: string) {
            const _vats = vats.slice();
            _vats[index].value = value;
            setVats(_vats);
        }
    }

    function onChangePercentage(index: number) {
        return function(newPercentage: string) {
            const _vats = vats.slice();
            _vats[index].percentage = newPercentage;
            setVats(_vats);
        }
    }

    function onDeleteVat(index: number) {
        return function() {
            setVats(vats.toSpliced(index, 1));
        }
    }

    function onAddVat() {
        if (vats.length >= 5) return;
        setVats(vats.concat({percentage: '-1', value: '0'}))
    }

    function onClickExplanation() {
        const link = <Link
            route='https://www.belastingdienst.nl/wps/wcm/connect/bldcontentnl/belastingdienst/zakelijk/btw/btw_berekenen_aan_uw_klanten/waarover_btw_berekenen/combinatie_goederen_en_diensten/'>de
            Belastingdienst</Link>;
        const text = <label>Samengestelde BTW wordt toegepast wanneer 1 product uit meerdere artikelen bestaat, waarbij
            voor ieder artikel een ander BTW tarief geldt. Uiteraard gelden hier regels voor, meer informatie
            hierover is terug te vinden bij {link}.<br/><br/>Omdat wij van Bol geen informatie ontvangen over de
            samenstelling van de producten en BTW tarieven, kunnen wij niet automatisch samengestelde BTW tarieven
            toepassen. Het is daarom mogelijk om handmatig de BTW bedragen (per stuk) in te stellen, deze worden dan
            gebruikt voor de bepaling van de totale BTW.<br/><br/>Omdat dit geen automatisch proces is, dienen de BTW
            bedragen handmatig bijgewerkt te worden wanneer de prijs bij Bol wijzigt. Het gebruik van samengestelde
            BTW is dan ook op eigen risico.</label>
        showDialog(text, 'Samengestelde BTW');
    }

    function _onSubmit() {
        if (!validateValues()) return;
        const submitValues: Record<string, number> = {};
        for (const vatData of vats) {
            const numberValue = Number(vatData.value.replace(',', '.'));
            if (vatData.percentage === '-1' || Number.isNaN(numberValue) || !numberValue) continue;
            submitValues[vatData.percentage] = numberValue;
        }
        onSubmit(submitValues, countryCode.current, productID.current);
        onClose();
    }

    function validateValues(): boolean {
        let validConfigs: number = 0;
        for (const vatData of vats) {
            const numberValue = Number(vatData.value.replace(',', '.'));
            if (vatData.percentage === '-1' || Number.isNaN(numberValue) || !numberValue) continue;
            if (++validConfigs >= 2) return true;
        }
        showDialog('Kies minimaal 2 BTW tarieven om samengestelde BTW in te stellen.', 'Samengestelde BTW');
        return false;
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <SplitVAT onChange={onChange} onChangePercentage={onChangePercentage} onClose={onClose} onSubmit={_onSubmit}
                  onAddVat={onAddVat} onDeleteVat={onDeleteVat} onClickExplanation={onClickExplanation} active={active}
                  vats={vats}/>
    );
}

export default forwardRef<ActivateHandle, Props>(SplitVATController);